/* =============================================================================
 * CCR core — shared namespace, storage helpers, UTM capture, consent gate.
 * Loaded first (defer). All other CCR modules depend on window.CCR.
 * No external dependencies. Honest CRO only — no cloaking / fake scarcity.
 * ========================================================================== */
(function () {
  'use strict';

  var CCR = (window.CCR = window.CCR || {});
  CCR.version = '1.0.0';

  /* ---------- read non-executable JSON data islands --------------------- *
   * Config/page-context/registry are rendered as <script type="application/
   * json"> islands (snippets/ccr-config.liquid, ccr-runtime-config.liquid) so
   * the storefront needs no inline executable script and can run under a
   * strict `script-src 'self'` CSP. We parse them here.                      */
  function readJSONIsland(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  CCR.config = window.CCR_CONFIG || readJSONIsland('ccr-config-data') || {};
  window.CCR_CONFIG = CCR.config;
  window.CCR_PAGE_CONTEXT = window.CCR_PAGE_CONTEXT ||
    readJSONIsland('ccr-page-context') || {};

  // Experiment registry: theme-settings override wins over the bundled island.
  (function () {
    var override = CCR.config.experiments_override;
    if (override) {
      try { window.CCR_EXPERIMENTS = JSON.parse(override); }
      catch (e) { if (window.console) console.warn('CCR: invalid experiments override', e); }
    }
    var runtime = readJSONIsland('ccr-runtime-config');
    if (runtime) {
      if (!window.CCR_EXPERIMENTS) window.CCR_EXPERIMENTS = runtime.experiments || [];
      window.CCR_OFFERS = window.CCR_OFFERS || runtime.offers || [];
      window.CCR_CAMPAIGNS = window.CCR_CAMPAIGNS || runtime.campaigns || [];
    }
  })();

  /* ---------- storage helpers (fail-safe) ------------------------------- */
  var store = {
    get: function (k) {
      try { return window.localStorage.getItem(k); } catch (e) { return null; }
    },
    set: function (k, v) {
      try { window.localStorage.setItem(k, v); } catch (e) {}
    },
    remove: function (k) {
      try { window.localStorage.removeItem(k); } catch (e) {}
    },
    cookie: function (k, v, days) {
      if (typeof v === 'undefined') {
        var m = document.cookie.match('(^|;)\\s*' + k + '\\s*=\\s*([^;]+)');
        return m ? decodeURIComponent(m.pop()) : null;
      }
      var d = new Date();
      d.setTime(d.getTime() + (days || 180) * 864e5);
      var secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie =
        k + '=' + encodeURIComponent(v) + ';expires=' + d.toUTCString() +
        ';path=/;SameSite=Lax' + secure;
    }
  };
  CCR.store = store;

  function hasStoredAnalyticsConsent() {
    try {
      var c = JSON.parse(store.get('ccr_consent') || 'null');
      return !!(c && c.analytics);
    } catch (e) { return false; }
  }

  var pendingUtm = null;

  /* ---------- UTM capture + cross-page persistence ---------------------- */
  CCR.utm = (function () {
    var KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term',
      'utm_content', 'gclid', 'fbclid', 'msclkid'];
    var saved = {};
    try { saved = JSON.parse(store.get('ccr_utm') || '{}'); } catch (e) {}
    var qs = new URLSearchParams(window.location.search);
    var touched = false;
    KEYS.forEach(function (k) {
      var v = qs.get(k);
      if (v) { saved[k] = v; touched = true; }
    });
    if (touched) {
      saved._first_landing = saved._first_landing || window.location.pathname;
      saved._captured_at = new Date().toISOString();
      pendingUtm = Object.assign({}, saved);
      if (hasStoredAnalyticsConsent()) {
        store.set('ccr_utm', JSON.stringify(saved));
        pendingUtm = null;
      }
    }
    return saved;
  })();

  /* ---------- consent gate ---------------------------------------------- *
   * Categories: analytics, ads. Default = denied until explicit grant
   * (privacy-first). Integrates with Shopify Customer Privacy API when
   * present, else uses first-party storage + banner.
   * California: GPC + optOutSale() map ads → sale_of_data for CPRA.         */
  var listeners = [];
  var DEFAULT = { analytics: false, ads: false, functional: true };
  var TRACKING_KEYS = ['ccr_cid', 'ccr_utm', 'ccr_exp_assignments'];

  function readConsent() {
    try {
      var c = JSON.parse(store.get('ccr_consent') || 'null');
      if (c) return c;
    } catch (e) {}
    return null;
  }

  var consentState = readConsent() || Object.assign({}, DEFAULT);

  var consent = {
    has: function (cat) { return !!consentState[cat]; },
    all: function () { return Object.assign({}, consentState); },
    purgeTrackingStorage: function () {
      TRACKING_KEYS.forEach(function (k) {
        store.remove(k);
        store.cookie(k, '', -1);
      });
    },
    set: function (partial) {
      var prevAnalytics = !!consentState.analytics;
      consentState = Object.assign({}, consentState, partial, { _set: true });
      store.set('ccr_consent', JSON.stringify(consentState));
      // Mirror into Shopify Customer Privacy API if available.
      try {
        if (window.Shopify && window.Shopify.customerPrivacy &&
          window.Shopify.customerPrivacy.setTrackingConsent) {
          window.Shopify.customerPrivacy.setTrackingConsent({
            analytics: !!consentState.analytics,
            marketing: !!consentState.ads,
            preferences: true,
            sale_of_data: !!consentState.ads
          }, function () {});
        }
      } catch (e) {}
      if (prevAnalytics && !consentState.analytics) {
        consent.purgeTrackingStorage();
      }
      listeners.forEach(function (fn) { try { fn(consent.all()); } catch (e) {} });
    },
    grantAll: function () { consent.set({ analytics: true, ads: true }); },
    denyAll: function () { consent.set({ analytics: false, ads: false }); },
    /* CPRA sale/share opt-out: force ads off. If undecided, deny both so the
     * banner does not re-prompt against an explicit opt-out signal. */
    optOutSale: function () {
      if (consent.isDecided()) {
        consent.set({ ads: false });
      } else {
        consent.set({ analytics: false, ads: false });
      }
    },
    isDecided: function () { return !!consentState._set; },
    onChange: function (fn) { listeners.push(fn); return fn; }
  };
  CCR.consent = consent;

  /* Global Privacy Control — treat as sale/share opt-out on every load. */
  function applyGpc() {
    try {
      if (typeof navigator === 'undefined' || !navigator.globalPrivacyControl) return;
    } catch (e) { return; }
    if (!consent.isDecided()) {
      consent.denyAll();
    } else {
      consent.optOutSale();
    }
  }
  applyGpc();

  /* Hydrate from Shopify Customer Privacy when localStorage has no decision.
   * Shopify values are "yes" | "no" | "" — only yes/no count as decided.
   * Goes through consent.set() so analytics listeners fire. One-shot
   * loadFeatures; no polling if the API never arrives. Skipped when GPC or
   * a prior banner choice already decided. */
  function shopifyYN(v) {
    if (v === 'yes') return true;
    if (v === 'no') return false;
    return null;
  }

  function hydrateFromShopifyPrivacy() {
    if (consent.isDecided()) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.globalPrivacyControl) {
        applyGpc();
        return;
      }
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.currentVisitorConsent !== 'function') return;
      var current = cp.currentVisitorConsent() || {};
      var analytics = shopifyYN(current.analytics);
      if (analytics === null) return;
      var ads = shopifyYN(current.marketing);
      consent.set({
        analytics: analytics,
        ads: ads === null ? false : ads
      });
      // GPC still wins over a Shopify "yes" for marketing/sale.
      applyGpc();
    } catch (e) {}
  }

  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function () { hydrateFromShopifyPrivacy(); }
      );
    } else {
      hydrateFromShopifyPrivacy();
    }
  } catch (e) {}

  if (pendingUtm) {
    consent.onChange(function (state) {
      if (state.analytics && pendingUtm) {
        store.set('ccr_utm', JSON.stringify(pendingUtm));
        pendingUtm = null;
      }
    });
  }

  /* ---------- client id (consent-aware persistence) --------------------- *
   * A first-party, non-PII pseudonymous id used for experiment assignment
   * and analytics correlation. It is persisted to storage ONLY once the
   * visitor has granted analytics consent (ePrivacy / GDPR: no non-essential
   * identifier before consent). Before that we use an in-memory, per-page id
   * so experiments still resolve without writing any tracking storage.       */
  function persistClientId(id) {
    store.set('ccr_cid', id);
    store.cookie('ccr_cid', id, 365);
  }
  CCR.clientId = (function () {
    var existing = store.get('ccr_cid') || store.cookie('ccr_cid');
    if (existing) return existing;
    var id = 'ccr-' + Date.now().toString(36) + '-' +
      Math.random().toString(36).slice(2, 8);
    if (consent.has('analytics')) {
      persistClientId(id);
    } else {
      // Persist later if/when analytics consent is granted.
      consent.onChange(function (state) {
        if (state.analytics && !store.get('ccr_cid')) persistClientId(id);
      });
    }
    return id;
  })();

  /* ---------- tiny pub/sub for in-page modules -------------------------- */
  var bus = {};
  CCR.on = function (evt, fn) { (bus[evt] = bus[evt] || []).push(fn); };
  CCR.emit = function (evt, data) {
    (bus[evt] || []).forEach(function (fn) { try { fn(data); } catch (e) {} });
  };

  /* DOM ready helper */
  CCR.ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* ---------- Cloudflare Turnstile (explicit render) -------------------- *
   * Site key comes from the runtime config island (public). Verification is
   * always server-side in the worker — never call siteverify from the browser. */
  CCR.turnstile = (function () {
    var scriptPromise = null;

    function siteKey() {
      var runtime = readJSONIsland('ccr-runtime-config');
      var key = runtime && runtime.chatbot && runtime.chatbot.turnstile_site_key;
      return key ? String(key) : '';
    }

    function loadScript() {
      if (window.turnstile) return Promise.resolve();
      if (scriptPromise) return scriptPromise;
      scriptPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.onload = function () { resolve(); };
        s.onerror = function () {
          scriptPromise = null;
          reject(new Error('turnstile_load_failed'));
        };
        document.head.appendChild(s);
      });
      return scriptPromise;
    }

    function widgetId(el) {
      return el ? el.getAttribute('data-widget-id') : '';
    }

    function render(el, opts) {
      opts = opts || {};
      var key = siteKey();
      if (!el || !key) return Promise.resolve(null);
      return loadScript().then(function () {
        var existing = widgetId(el);
        if (existing) return existing;
        var id = window.turnstile.render(el, {
          sitekey: key,
          theme: opts.theme || 'light',
          size: opts.size || 'normal',
          appearance: opts.appearance || 'always',
          callback: function (token) {
            el.setAttribute('data-ccr-turnstile-token', token || '');
            if (typeof opts.callback === 'function') opts.callback(token);
            if (el._ccrTsResolve) {
              var resolve = el._ccrTsResolve;
              el._ccrTsResolve = null;
              resolve(token || '');
            }
          },
          'expired-callback': function () {
            el.removeAttribute('data-ccr-turnstile-token');
            if (typeof opts.expiredCallback === 'function') opts.expiredCallback();
          },
          'error-callback': function () {
            el.removeAttribute('data-ccr-turnstile-token');
            if (el._ccrTsResolve) {
              var resolveErr = el._ccrTsResolve;
              el._ccrTsResolve = null;
              resolveErr('');
            }
            if (typeof opts.errorCallback === 'function') opts.errorCallback();
          }
        });
        el.setAttribute('data-widget-id', id);
        return id;
      });
    }

    function getResponse(el) {
      if (!el || !window.turnstile) {
        return (el && el.getAttribute('data-ccr-turnstile-token')) || '';
      }
      var id = widgetId(el);
      if (!id) return el.getAttribute('data-ccr-turnstile-token') || '';
      try { return window.turnstile.getResponse(id) || ''; } catch (e) { return ''; }
    }

    function reset(el) {
      if (!el || !window.turnstile) return;
      el.removeAttribute('data-ccr-turnstile-token');
      var id = widgetId(el);
      if (!id) return;
      try { window.turnstile.reset(id); } catch (e) {}
    }

    /** Resolve with a fresh token. Invisible widgets call execute(). */
    function getToken(el, opts) {
      opts = opts || {};
      if (!siteKey()) return Promise.resolve('');
      return render(el, opts).then(function (id) {
        if (!id || !window.turnstile) return '';
        var existing = getResponse(el);
        if (existing && opts.size !== 'invisible') return existing;
        return new Promise(function (resolve) {
          el._ccrTsResolve = resolve;
          try {
            if (opts.size === 'invisible' || opts.execute) {
              window.turnstile.execute(id);
            } else {
              // Managed: wait briefly for an already-completed challenge.
              var token = getResponse(el);
              if (token) {
                el._ccrTsResolve = null;
                resolve(token);
                return;
              }
            }
          } catch (e) {
            el._ccrTsResolve = null;
            resolve('');
            return;
          }
          setTimeout(function () {
            if (!el._ccrTsResolve) return;
            el._ccrTsResolve = null;
            resolve(getResponse(el));
          }, 8000);
        });
      });
    }

    return {
      siteKey: siteKey,
      loadScript: loadScript,
      render: render,
      getResponse: getResponse,
      reset: reset,
      getToken: getToken
    };
  })();
})();
